import React, { ChangeEvent, Fragment, useRef, useState } from 'react'
import { Dialog, DialogPanel, Transition } from '@headlessui/react'
import { DateTime } from 'luxon';
import { AppBanner, AppBannerCallToActionType, AppBannerSize, PointOfSale } from '@m4a3/api-client';
import ImageDragDropWithPreview from '../../../components/ImageDragDropWithPreview/ImageDragDropWithPreview';
import { uploadImage } from '../../../actions/partnerActions';
import FilteredDropdown from '../../../components/FilteredDropdown/FilteredDropdown';
import { orderfulApi } from '../../../services/api-instances';

interface AppBannerModalProps {
  readonly onClose: () => void;
  readonly cellId: number;
  readonly timezone: string;
  readonly appBanner?: AppBanner;
  readonly pointOfSales: PointOfSale[];
}

export default function AppBannerModalModal(props: AppBannerModalProps) {
  const isEditing = !!props.appBanner;
  const [open, setOpen] = useState<boolean>(true)
  const [selectedMerchantId, setSelectedMerchantId] = useState<number | null>(isEditing ? props.appBanner.merchantId : null);
  const [url, setUrl] = useState<string>(isEditing ? props.appBanner.url ?? '' : '');
  const [backgroundImage, setBackgroundImage] = useState<string>(isEditing ? props.appBanner.backgroundImageUri : '');
  const [displayFrom, setDisplayFrom] = useState<string>(isEditing ? DateTime.fromISO(props.appBanner.displayFrom).toFormat(`yyyy-MM-dd'T'HH:mm`) : DateTime.now().toFormat(`yyyy-MM-dd'T'HH:mm`));
  const [displayUntil, setDisplayUntil] = useState<string>(isEditing ? DateTime.fromISO(props.appBanner.displayUntil).toFormat(`yyyy-MM-dd'T'HH:mm`) : DateTime.now().plus({hour: 1}).toFormat(`yyyy-MM-dd'T'HH:mm`));
  const [priority, setPriority] = useState<number | null>(isEditing ? props.appBanner.priority : null);

  const cancelButtonRef = useRef<HTMLButtonElement>  (null)

  const handleStartTimeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const time = event.target.value;
    if (time) {
      setDisplayFrom(time);
    } else {
      setDisplayFrom('');
    }
  };

  const handleEndTimeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const time = event.target.value;

    if (time) {
      setDisplayUntil(time);
    } else {
      setDisplayUntil('');
    }
  };

  const handleMerchantChange = (merchantId: number) => {
    setSelectedMerchantId(merchantId > 0 ? merchantId : null);
    // Clear URL when merchant is selected
    if (merchantId > 0) {
      setUrl('');
    }
  };

  const handleUrlChange = (event: ChangeEvent<HTMLInputElement>) => {
    const urlValue = event.target.value;
    setUrl(urlValue);
    // Clear merchant when URL is entered
    if (urlValue && urlValue.trim().length > 0) {
      setSelectedMerchantId(null);
    }
  };

  const handleNoActionClick = () => {
    setSelectedMerchantId(null);
    setUrl('');
  };

  const onPressSaveAppBanner = async () => {
    
    if (isEditing) {
      await updateAppBanner();
    } else {
      await saveAppBanner();
    }
    setTimeout(() => {
      props.onClose();
    }, 100);
  };


  const timeStringToISO = (time: string): string => {
    const now = DateTime.local();
    let day = `${now.day}`;
    let month = `${now.month}`;
    let year = `${now.year}`;
    if (time.includes('T')) {
      const dateParts = time.split('T')[0].split('-');
      day = dateParts[2];
      month = dateParts[1];
      year = dateParts[0];
      time = time.split('T')[1];
    }
    const [hours, minutes] = time.split(':').map(Number);
    const updatedDateTime = now.set({
      day: Number(day),
      month: Number(month),
      year: Number(year),
      hour: hours,
      minute: minutes,
      second: 0,
      millisecond: 0
    });

    return updatedDateTime.toJSDate().toISOString();
  };

  const getCallToActionType = () => {

    if (url && url.trim().length > 0) {
      return AppBannerCallToActionType.OPEN_URL;
    } else if (selectedMerchantId && selectedMerchantId > 0) {
      return AppBannerCallToActionType.OPEN_STORE;
    } else {
      return AppBannerCallToActionType.NO_ACTION;
    }
  }

  const saveAppBanner = async () => {
    await orderfulApi.createAppBanner({
      createdBy: null,
      size: AppBannerSize.LARGE,
      backgroundImageUri: backgroundImage,
      displayFrom: new Date(displayFrom).toISOString(),
      displayUntil: new Date(displayUntil).toISOString(),
      backgroundColor: null,
      title: null,
      body: null,
      titleTextColor: null,
      bodyTextColor: null,
      callToActionButtonText: null,
      callToActionButtonBackgroundColor: null,
      callToActionButtonTextColor: null,
      callToActionType: getCallToActionType(),
      limitToCellId: props.cellId,
      merchantId: selectedMerchantId > 0 ? selectedMerchantId : null,
      url: url && url.trim().length > 0 ? url : null,
      priority: priority,
    })
  };

  const updateAppBanner = async () => {
    await orderfulApi.updateAppBanner(props.appBanner.id, {
      ...props.appBanner!,
      backgroundImageUri: backgroundImage,
      displayFrom: timeStringToISO(displayFrom),
      displayUntil: timeStringToISO(displayUntil),
      callToActionType: url && url.trim().length > 0 ? AppBannerCallToActionType.OPEN_URL : AppBannerCallToActionType.OPEN_STORE,
      limitToCellId: props.cellId,
      merchantId: selectedMerchantId > 0 ? selectedMerchantId : null,
      url: url && url.trim().length > 0 ? url : null,
      priority: priority,
    })
  };

  const uploadAndSetBackgroundImage = async (imageFile: File) => {
    const formData = new FormData();
    formData.append('image', imageFile, imageFile.name);

    const result = await uploadImage(formData, imageFile.name);
    setBackgroundImage(result.fileUrl)
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-10" initialFocus={cancelButtonRef} onClose={props.onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-20 sm:w-full sm:max-w-lg">
                <div className="bg-white px-4 pb-4 pt-8 sm:p-6 sm:pt-8 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mt-1 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                      <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900 mb-6">
                        App Banner
                      </Dialog.Title>
                      <div className="mt-2">
                        <div className="mt-4 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                          <div className="sm:col-span-3">
                            <label htmlFor="start-time" className="block text-sm font-medium leading-6 text-gray-900">
                              Start time
                            </label>
                            <div className="mt-2">
                              <input
                                id="start-time"
                                type="datetime-local"
                                value={displayFrom}
                                onChange={handleStartTimeChange}
                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                              />
                            </div>
                          </div>

                          <div className="sm:col-span-3">
                            <label htmlFor="end-time" className="block text-sm font-medium leading-6 text-gray-900">
                              End time
                            </label>
                            <div className="mt-2">
                              <input
                                type="datetime-local"
                                name="end-time"
                                id="end-time"
                                value={displayUntil}
                                onChange={handleEndTimeChange}
                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                              />
                            </div>
                          </div>
                          
                          {/* Grouped Merchant/URL Section */}
                          <div className="sm:col-span-6">
                            <fieldset className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                              <legend className="text-sm font-medium text-gray-900 px-2 bg-white">
                                Action Target (Choose one)
                              </legend>
                              <div className="space-y-4 mt-2">
                                <div>
                                  <FilteredDropdown
                                    label="Merchant"
                                    value={selectedMerchantId || 0}
                                    onChange={handleMerchantChange}
                                    options={props.pointOfSales?.map((pointOfSale: PointOfSale) => ({
                                      value: pointOfSale.id,
                                      displayText: pointOfSale.posName,
                                    }))} />
                                </div>
                                
                                <div className="relative">
                                  <div className="absolute inset-0 flex items-center">
                                    <div className="w-full border-t border-gray-300" />
                                  </div>
                                  <div className="relative flex justify-center text-xs">
                                    <span className="bg-gray-50 px-2 text-gray-500">OR</span>
                                  </div>
                                </div>
                                
                                <div>
                                  <label htmlFor="url" className="block text-sm font-medium leading-6 text-gray-900">
                                    URL
                                  </label>
                                  <div className="mt-2">
                                    <input
                                      id="url"
                                      type="url"
                                      value={url}
                                      onChange={handleUrlChange}
                                      placeholder="https://example.com"
                                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                                    />
                                  </div>
                                </div>
                                
                                <div className="relative">
                                  <div className="absolute inset-0 flex items-center">
                                    <div className="w-full border-t border-gray-300" />
                                  </div>
                                  <div className="relative flex justify-center text-xs">
                                    <span className="bg-gray-50 px-2 text-gray-500">OR</span>
                                  </div>
                                </div>
                                
                                <div>
                                  <button
                                    type="button"
                                    onClick={handleNoActionClick}
                                    className="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                  >
                                    No Action
                                  </button>
                                </div>
                              </div>
                              <p className="mt-3 text-sm text-gray-600">
                                Select a merchant to open their store with the banner, enter a URL to open a custom link, or choose no action.
                              </p>
                            </fieldset>
                          </div>

                          <div className="sm:col-span-6">
                            <label htmlFor="priority" className="block text-sm font-medium leading-6 text-gray-900">
                              Priority
                            </label>
                            <div className="mt-2">
                              <input
                                id="priority"
                                type="number"
                                value={priority ?? ''}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  setPriority(value === '' ? null : parseInt(value, 10));
                                }}
                                placeholder="Optional priority (lower numbers = higher priority)"
                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                              />
                            </div>
                            <p className="mt-1 text-sm text-gray-500">
                              Optional. Lower numbers = higher priority.
                            </p>
                          </div>
                          <div className="sm:col-span-6">
                            <div className="mt-2">
                              <ImageDragDropWithPreview
                                label={'Image'}
                                previewImageUrl={backgroundImage}
                                onChange={(file: File) => uploadAndSetBackgroundImage(file)} />
                            </div>
                          </div>
                        </div>

                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                  <button
                    type="button"
                    className={`mr-3 inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold shadow-sm sm:ml-3 sm:w-auto ${
                      'bg-blue-600 text-white hover:bg-blue-500' 
                    }`}
                    onClick={() => onPressSaveAppBanner()}
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    className="sm:ml-auto mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                    onClick={() => props.onClose()}
                    ref={cancelButtonRef}
                  >
                    Cancel
                  </button>
                </div>
              </DialogPanel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
